import {ResolvedNewJoiner} from "../../api/ResolvedNewJoinerStore";
import React, {useCallback, useMemo} from "react";
import {patchKey, useKeyTransactionStore} from "../../api/KeyTransactionStore";
import {useMessage} from "../../util/actions/Message";
import {today} from "../../util/DateUtil";
import {ChecklistGroup} from "./NewJoinerChecklistItem";
import {IconButton, Link, Stack, Typography} from "@mui/material";
import {KeyChip} from "../../components/key-chip/KeyChip";
import RemoveIcon from "@mui/icons-material/Close";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {AssignKeyDialog} from "./AssignKeyDialog";
import {NewJoinerOperations} from "./NewJoinerApi";
import {ChecklistItemCategory} from "../../api/Model";
import {toEmployeeRef} from "../../api/Projections";


export function KeyChecklistGroup({newJoiner, operations}: {
    newJoiner: ResolvedNewJoiner,
    operations: NewJoinerOperations,
}) {
    const executeTx = useKeyTransactionStore(state => state.execute);
    const message = useMessage();

    const handleAssignKey = useFormDialogAction({
        title: 'Assign Key',
        description: 'Assign a key to the new joiner',
        submitButtonText: 'Assign',
        fullWidth: true,
        renderForm: AssignKeyDialog,
        action: openDialog => openDialog({
            initialValue: {keySnapshot: undefined},
            onSubmit: async form => {
                if (form.keySnapshot && newJoiner.employee) {
                    await executeTx([
                        patchKey(form.keySnapshot, today(), {
                            location: {
                                type: 'with-employee',
                                employee: toEmployeeRef(newJoiner.employee),
                                expTo: undefined
                            },
                        })
                    ]);
                }
            },
            successMessage: form => `Key ${form?.keySnapshot?.key?.keyNumber} assigned`,
            errorMessage: 'Failed to assign key. Please try again later.',
        }),
    }, [newJoiner, executeTx]);

    const handleReturnKey = useCallback(() =>
        message.exec(
            () => executeTx(operations.keys.map(snapshot =>
                patchKey(snapshot, today(), {
                    location: {type: 'with-vault'},
                }))),
            `Keys returned to vault`,
            'Failed to return keys. Please try again later.',
        ), [executeTx, message, operations]);

    const toggles = useMemo(
        () => operations.checklistToggles[ChecklistItemCategory.Key],
        [operations.checklistToggles],
    );


    return (
        <ChecklistGroup
            headerChecked={operations.keys.length > 0}
            disabled={!newJoiner.employee}
            nestedToggles={toggles}
        >{operations.keys.length <= 0 ? (
            newJoiner.employee && operations.canOperateKeys ? (
                <Link onClick={handleAssignKey}>Assign Key</Link>
            ) : (
                <Typography color="textDisabled">Assign Key</Typography>
            )
        ) : (
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                {operations.keys.map(key => (
                    <KeyChip
                        key={`${key.key.id}-${key.historyRecord.id}`}
                        keyData={key.key}
                        record={key.historyRecord}
                        size="small"
                    />
                ))}
                {operations.canOperateKeys && (
                    <IconButton
                        onClick={handleReturnKey}
                        sx={{height: 20, width: 20}}
                        title="Return keys to vault"
                    >
                        <RemoveIcon sx={{fontSize: 16, color: 'text.secondary'}}/>
                    </IconButton>
                )}
            </Stack>
        )}</ChecklistGroup>
    );
}
